import React, { useState, useEffect, useCallback } from 'react';
import { alertsApi } from '../lib/api';
import { useSocket } from '../contexts/SocketContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import {
  Bell,
  Loader2,
  AlertTriangle,
  AlertCircle,
  Info,
  CheckCircle2,
  Clock,
  RefreshCw,
} from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { fr } from 'date-fns/locale';
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationPrevious,
  PaginationNext,
  PaginationLink,
} from '../components/ui/pagination';

const PAGE_SIZE = 20;

const AlertsPage = () => {
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('active');
  const [page, setPage] = useState(1);
  const { socket } = useSocket();

  const fetchAlerts = useCallback(async () => {
    try {
      const params = activeTab !== 'all' ? { status: activeTab } : {};
      const response = await alertsApi.getAll(params);
      setAlerts(response.data);
      setPage(1);
    } catch (error) {
      toast.error('Erreur lors du chargement des alertes');
    } finally {
      setLoading(false);
    }
  }, [activeTab]);

  useEffect(() => {
    fetchAlerts();
  }, [fetchAlerts]);

  // Real-time updates
  useEffect(() => {
    if (!socket) return;

    socket.on('new_alert', () => {
      fetchAlerts();
    });

    socket.on('alert_acknowledged', () => {
      fetchAlerts();
    });

    socket.on('alert_resolved', () => {
      fetchAlerts();
    });

    return () => {
      socket.off('new_alert');
      socket.off('alert_acknowledged');
      socket.off('alert_resolved');
    };
  }, [socket, fetchAlerts]);

  const handleAcknowledge = async (alertId) => {
    try {
      await alertsApi.acknowledge(alertId);
      toast.success('Alerte acquittée');
      fetchAlerts();
    } catch (error) {
      toast.error("Erreur lors de l'acquittement");
    }
  };

  const handleResolve = async (alertId) => {
    try {
      await alertsApi.resolve(alertId);
      toast.success('Alerte résolue');
      fetchAlerts();
    } catch (error) {
      toast.error('Erreur lors de la résolution');
    }
  };

  const severityConfig = {
    critical: {
      label: 'Critique',
      icon: AlertTriangle,
      color: 'bg-red-500 text-white',
      bgColor: 'alert-critical',
    },
    warning: {
      label: 'Attention',
      icon: AlertCircle,
      color: 'bg-amber-500 text-white',
      bgColor: 'alert-warning',
    },
    info: {
      label: 'Info',
      icon: Info,
      color: 'bg-blue-500 text-white',
      bgColor: 'alert-info',
    },
  };

  const statusLabels = {
    active: 'Active',
    acknowledged: 'Acquittée',
    resolved: 'Résolue',
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    try {
      const date = new Date(dateStr);
      return formatDistanceToNow(date, { addSuffix: true, locale: fr });
    } catch {
      return '';
    }
  };

  const activeCount = alerts.filter((a) => a.status === 'active').length;
  const acknowledgedCount = alerts.filter((a) => a.status === 'acknowledged').length;
  const resolvedCount = alerts.filter((a) => a.status === 'resolved').length;

  const totalPages = Math.ceil(alerts.length / PAGE_SIZE);
  const paginatedAlerts = alerts.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[calc(100vh-4rem)]">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="alerts-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-semibold tracking-tight">Alertes</h1>
          <p className="text-muted-foreground mt-1">Gérez les alertes de vos capteurs</p>
        </div>
        <Button variant="outline" onClick={fetchAlerts} data-testid="refresh-alerts-btn">
          <RefreshCw className="mr-2 h-4 w-4" />
          Actualiser
        </Button>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="active" data-testid="tab-active">
            Actives
            {activeCount > 0 && (
              <Badge variant="destructive" className="ml-2">
                {activeCount}
              </Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="acknowledged" data-testid="tab-acknowledged">
            Acquittées
            {acknowledgedCount > 0 && (
              <Badge variant="secondary" className="ml-2">
                {acknowledgedCount}
              </Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="resolved" data-testid="tab-resolved">
            Résolues
          </TabsTrigger>
          <TabsTrigger value="all" data-testid="tab-all">
            Toutes
          </TabsTrigger>
        </TabsList>

        <TabsContent value={activeTab} className="mt-6">
          {alerts.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center">
                <CheckCircle2 className="h-12 w-12 mx-auto text-emerald-500 mb-4" />
                <p className="text-muted-foreground">Aucune alerte</p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4">
              {paginatedAlerts.map((alert) => {
                const severity = severityConfig[alert.severity] || severityConfig.info;
                const SeverityIcon = severity.icon;

                return (
                  <Card
                    key={alert.id}
                    className={`${severity.bgColor} transition-all`}
                    data-testid={`alert-card-${alert.id}`}
                  >
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex items-start gap-4 flex-1">
                          <div
                            className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full ${
                              alert.severity === 'critical'
                                ? 'bg-red-100 dark:bg-red-950/50'
                                : alert.severity === 'warning'
                                ? 'bg-amber-100 dark:bg-amber-950/50'
                                : 'bg-blue-100 dark:bg-blue-950/50'
                            }`}
                          >
                            <SeverityIcon
                              className={`h-5 w-5 ${
                                alert.severity === 'critical'
                                  ? 'text-red-600 dark:text-red-400'
                                  : alert.severity === 'warning'
                                  ? 'text-amber-600 dark:text-amber-400'
                                  : 'text-blue-600 dark:text-blue-400'
                              }`}
                            />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1">
                              <Badge className={severity.color}>{severity.label}</Badge>
                              <Badge variant="outline">{statusLabels[alert.status]}</Badge>
                            </div>
                            <h3 className="font-semibold text-foreground">{alert.title}</h3>
                            <p className="text-sm text-muted-foreground mt-1">
                              {alert.message}
                            </p>
                            <div className="flex items-center gap-4 mt-3 text-xs text-muted-foreground">
                              <span className="flex items-center gap-1">
                                <Clock className="h-3 w-3" />
                                {formatDate(alert.created_at)}
                              </span>
                              {alert.acknowledged_at && (
                                <span>Acquittée {formatDate(alert.acknowledged_at)}</span>
                              )}
                              {alert.resolved_at && (
                                <span>Résolue {formatDate(alert.resolved_at)}</span>
                              )}
                            </div>
                          </div>
                        </div>
                        <div className="flex gap-2 shrink-0">
                          {alert.status === 'active' && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleAcknowledge(alert.id)}
                              data-testid={`acknowledge-btn-${alert.id}`}
                            >
                              <CheckCircle2 className="mr-1 h-4 w-4" />
                              Acquitter
                            </Button>
                          )}
                          {(alert.status === 'active' || alert.status === 'acknowledged') && (
                            <Button
                              size="sm"
                              onClick={() => handleResolve(alert.id)}
                              data-testid={`resolve-btn-${alert.id}`}
                            >
                              Résoudre
                            </Button>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}

          {totalPages > 1 && (
            <Pagination className="mt-6">
              <PaginationContent>
                <PaginationItem>
                  <PaginationPrevious
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    className={page === 1 ? 'pointer-events-none opacity-50' : 'cursor-pointer'}
                  />
                </PaginationItem>
                {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
                  <PaginationItem key={p}>
                    <PaginationLink
                      isActive={p === page}
                      onClick={() => setPage(p)}
                      className="cursor-pointer"
                    >
                      {p}
                    </PaginationLink>
                  </PaginationItem>
                ))}
                <PaginationItem>
                  <PaginationNext
                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                    className={page === totalPages ? 'pointer-events-none opacity-50' : 'cursor-pointer'}
                  />
                </PaginationItem>
              </PaginationContent>
            </Pagination>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default AlertsPage;
